import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { apiFetch, logEvent } from '../api';

function AppealHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [view, setView] = useState('active'); // 'active' or 'archived'
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const user = await getCurrentUser();
      const response = await apiFetch(`/get-appeals/${user.userId}`);
      if (!response.ok) throw new Error('Failed to load appeals');
      const data = await response.json();
      setHistory(data.appeals || []);
    } catch (error) {
      console.log('Error fetching history:', error);
    }
    setLoading(false);
  };

  const handleCopy = (item) => {
    const confirmed = window.confirm(
      'I confirm that a licensed healthcare professional has reviewed and validated this appeal text before use.'
    );
    if (!confirmed) return;
    navigator.clipboard.writeText(item.appeal_letter);
    logEvent('copy_appeal', item.id);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const setArchived = async (item, archived) => {
    if (archived) {
      const confirmed = window.confirm(
        `Archive the appeal for ${item.patient_name}? You can restore it from the Archived tab.`
      );
      if (!confirmed) return;
    }
    setBusyId(item.id);
    try {
      const response = await apiFetch(`/update-appeal/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived }),
      });
      if (!response.ok) throw new Error('Update failed');
      setHistory((prev) =>
        prev.map((a) => (a.id === item.id ? { ...a, archived } : a))
      );
    } catch (error) {
      alert('Could not update this appeal. Please try again.');
    }
    setBusyId(null);
  };

  const handleLogout = async () => {
    await logEvent('logout');
    await signOut();
    navigate('/login');
  };

  const activeCount = history.filter((item) => !item.archived).length;
  const archivedCount = history.length - activeCount;

  const filteredHistory = history
    .filter((item) => (view === 'archived' ? item.archived : !item.archived))
    .filter((item) =>
      (item.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

  const statusColors = {
    pending: { bg: '#fef3c7', text: '#92400e' },
    approved: { bg: '#d1fae5', text: '#065f46' },
    denied: { bg: '#fee2e2', text: '#991b1b' },
  };

  const tabStyle = (active) => ({
    padding: '10px 18px',
    backgroundColor: 'transparent',
    color: active ? '#1a365d' : '#718096',
    border: 'none',
    borderBottom: active ? '3px solid #1a365d' : '3px solid transparent',
    fontWeight: active ? 'bold' : 'normal',
    fontSize: '15px',
    cursor: 'pointer',
    marginBottom: '-2px',
  });

  const smallButton = {
    padding: '4px 10px',
    backgroundColor: '#edf2f7',
    color: '#1a365d',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  };

  const emptyMessage = () => {
    if (searchTerm && filteredHistory.length === 0) return 'No patients match your search.';
    if (view === 'archived') return 'No archived appeals.';
    if (history.length === 0) return 'No appeals yet. Click New Appeal to create your first one.';
    return 'No active appeals.';
  };

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #1a365d',
        paddingBottom: '15px'
      }}>
        <h1 style={{ color: '#1a365d', margin: 0 }}>Clinix AI</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/appeal')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#edf2f7',
              color: '#1a365d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            New Appeal
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <h2 style={{ color: '#2d3748', marginBottom: '12px' }}>Past Appeals</h2>

      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '16px' }}>
        <button style={tabStyle(view === 'active')} onClick={() => setView('active')}>
          Active ({activeCount})
        </button>
        <button style={tabStyle(view === 'archived')} onClick={() => setView('archived')}>
          Archived ({archivedCount})
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by patient name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          width: '100%',
          padding: '10px',
          marginBottom: '16px',
          borderRadius: '6px',
          border: '1px solid #ddd',
          fontSize: '14px',
          boxSizing: 'border-box'
        }}
      />

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        {loading ? (
          <p style={{ color: '#718096' }}>Loading...</p>
        ) : filteredHistory.length === 0 ? (
          <p style={{ color: '#718096' }}>{emptyMessage()}</p>
        ) : (
          filteredHistory.map((item) => (
            <div
              key={item.id}
              style={{
                padding: '14px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong
                  onClick={() => navigate(`/appeal-detail/${item.id}`)}
                  style={{ color: '#1a365d', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {item.patient_name}
                </strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    padding: '2px 10px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    backgroundColor: statusColors[item.status]?.bg || '#f0f0f0',
                    color: statusColors[item.status]?.text || '#333',
                    textTransform: 'capitalize'
                  }}>
                    {item.status || 'pending'}
                  </span>
                  <span style={{ color: '#718096', fontSize: '13px' }}>
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <p style={{ color: '#4a5568', fontSize: '14px', margin: '4px 0' }}>
                {item.requested_medication} — {item.diagnosis}
              </p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                <button onClick={() => handleCopy(item)} style={smallButton}>
                  {copiedId === item.id ? 'Copied!' : 'Copy Appeal'}
                </button>
                <button
                  onClick={() => setArchived(item, !item.archived)}
                  disabled={busyId === item.id}
                  style={{
                    ...smallButton,
                    backgroundColor: 'white',
                    color: '#4a5568',
                    border: '1px solid #cbd5e0',
                    opacity: busyId === item.id ? 0.6 : 1,
                  }}
                >
                  {busyId === item.id ? 'Saving...' : item.archived ? 'Restore' : 'Archive'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AppealHistory;